import { Button, Dialog, DialogContent, Divider, styled, Typography } from "@mui/material";
import { MeetingViewQuery } from "../queries.generated";
import { useMemo, useState } from "react";
import { Duration, duration } from "moment";
import moment from "moment/moment";
import { useInterval, useTimeout } from "@dvrt/react-tools";
import { FormattedMessage } from "react-intl";
import { createUtcMoment } from "conference/tools";
import { RootContainer } from "./widgets";
import { getMeetingEndTime, getMeetingJoinTime } from "./meetingDataErrors";
import { formatDuration, useSettings } from "./tools";

type Meeting = MeetingViewQuery["meetings"]["items"][number];

const DetailsDivider = styled("div")({
    display: "flex",
    alignItems: "center",
    marginTop: "1em",
});

const DetailsContent = styled("table")({
    "& td:first-child": {
        paddingRight: "1em",
    },
});

const UntilJoinTypography = styled(Typography)(({ theme }) => ({
    padding: "0.7em 1em",
    backgroundColor: theme.palette.info.main,
    borderRadius: theme.shape.borderRadius,
}));

const JoinDiv = styled("div")({
    display: "flex",
    justifyContent: "center",
});

const UntilToJoin = ({ untilJoin, detailed }: { detailed: boolean; untilJoin: Duration }) => {
    const durationString = detailed ? `in ${formatDuration(untilJoin)}` : untilJoin.humanize(true);

    return (
        <UntilJoinTypography variant="h6">
            <FormattedMessage
                id="meetingPage.meetingIsNotRunning.untilJoin"
                defaultMessage="You can join to this meeting {duration}"
                values={{ duration: durationString }}
            />
        </UntilJoinTypography>
    );
};

const TooEarly = ({ meeting }: { meeting: Meeting }) => {
    const joinTime = getMeetingJoinTime(meeting);
    const [untilJoin, setUntilJoin] = useState(duration(joinTime.diff(moment())));
    const settings = useSettings();

    const canJoin = untilJoin.asHours() < 0;
    const startTime = createUtcMoment(meeting.startTime).local();
    const started = startTime.isBefore();

    useInterval(() => {
        setUntilJoin(duration(joinTime.diff(moment())));
    }, 1000);

    return (
        <>
            <div style={{ padding: "12px 20px 8px" }}>
                {!canJoin && (
                    <>
                        <Typography variant="h5">
                            <FormattedMessage id="meetingPage.meetingIsNotRunning.title" defaultMessage="Oh no!" />
                        </Typography>
                        <Typography variant="subtitle1">
                            <FormattedMessage
                                id="meetingPage.meetingIsNotRunning.notStartedYet"
                                defaultMessage="This meeting is not started yet."
                            />
                        </Typography>
                    </>
                )}
                {canJoin && (
                    <>
                        <Typography variant="h5">
                            <FormattedMessage
                                id="meetingPage.meetingIsNotRunning.canJoin.title"
                                defaultMessage="You can join now!"
                            />
                        </Typography>
                        <Typography variant="subtitle1">
                            {started && (
                                <FormattedMessage
                                    id="meetingPage.meetingIsNotRunning.canJoin.started"
                                    defaultMessage="The meeting has been started {duration} ago"
                                    values={{
                                        duration: formatDuration(duration(moment().diff(startTime))),
                                    }}
                                />
                            )}
                            {!started && (
                                <FormattedMessage
                                    id="meetingPage.meetingIsNotRunning.canJoin.notStartedYet"
                                    defaultMessage="The meeting will start in {duration}"
                                    values={{
                                        duration: formatDuration(duration(startTime.diff(moment()))),
                                    }}
                                />
                            )}
                        </Typography>
                    </>
                )}
            </div>
            <Divider />
            <DialogContent style={{ padding: "1.5em 20px" }}>
                {!canJoin && <UntilToJoin detailed={untilJoin.asHours() < 1} untilJoin={untilJoin} />}
                {canJoin && (
                    <JoinDiv>
                        <Button variant="contained" color="primary" onClick={() => settings.update()}>
                            <FormattedMessage id="meeting.join" defaultMessage="Join meeting" />
                        </Button>
                    </JoinDiv>
                )}
                <Details meeting={meeting} />
            </DialogContent>
        </>
    );
};

const TooLate = ({ meeting }: { meeting: Meeting }) => {
    return (
        <>
            <div style={{ padding: "12px 20px 8px" }}>
                <Typography variant="h5">
                    <FormattedMessage id="meetingPage.meetingIsNotRunning.title" defaultMessage="Oh no!" />
                </Typography>
                <Typography variant="subtitle1">
                    <FormattedMessage
                        id="meetingPage.meetingIsNotRunning.isOver"
                        defaultMessage="The meeting is over."
                    />
                </Typography>
            </div>
            <Divider />
            <DialogContent style={{ padding: "0 20px 1.5em" }}>
                <Details meeting={meeting} />
            </DialogContent>
        </>
    );
};

const Details = ({ meeting }: { meeting: Meeting }) => {
    const startTime = createUtcMoment(meeting.startTime).local();
    const endTime = startTime.clone().add(meeting.length, "minutes");

    return (
        <>
            <DetailsDivider>
                <Typography variant="subtitle1">
                    <FormattedMessage id="details" defaultMessage="Details" />
                </Typography>
                <Divider style={{ flexGrow: 1, marginLeft: "1em" }} />
            </DetailsDivider>
            <DetailsContent>
                <tbody>
                    <tr>
                        <td>
                            <Typography>
                                <FormattedMessage id="summary" defaultMessage="Summary" />:
                            </Typography>
                        </td>
                        <td>{meeting.summary}</td>
                    </tr>
                    <tr>
                        <td>
                            <Typography>
                                <FormattedMessage id="time.start" defaultMessage="Start time" />:
                            </Typography>
                        </td>
                        <td>{startTime.format("YYYY-MM-DD HH:mm")}</td>
                    </tr>
                    <tr>
                        <td>
                            <Typography>
                                <FormattedMessage id="time.end" defaultMessage="End time" />:
                            </Typography>
                        </td>
                        <td>{endTime.format("YYYY-MM-DD HH:mm")}</td>
                    </tr>
                </tbody>
            </DetailsContent>
        </>
    );
};

export const MeetingIsNotRunning = ({ meeting }: { meeting: Meeting }) => {
    const joinTime = getMeetingJoinTime(meeting);
    const [isTooEarly, setIsTooEarly] = useState(!joinTime.isBefore());
    const delay = useMemo(() => duration(getMeetingEndTime(meeting).diff(moment())).asMilliseconds(), []);

    useTimeout(() => {
        setIsTooEarly(false);
    }, delay ?? null);

    return (
        <RootContainer>
            <Dialog open={true}>{isTooEarly ? <TooEarly meeting={meeting} /> : <TooLate meeting={meeting} />}</Dialog>
        </RootContainer>
    );
};
